
/**
 * Debug Manager - Centralized debugging system
 * Provides controlled debug logging, error tracking, and performance monitoring
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface DebugOptions {
  enabled: boolean;
  logToConsole: boolean;
  persistLogs: boolean;
  maxLogEntries: number;
  includeTimestamps: boolean;
  logLevel: LogLevel;
}

class DebugManager {
  private static instance: DebugManager;
  private logs: Array<{level: LogLevel, component: string, message: string, data?: any, timestamp: Date}> = [];
  private errors: Array<{error: Error, component: string, timestamp: Date}> = [];
  private performanceMarks: Record<string, number> = {};
  private options: DebugOptions = {
    enabled: process.env.NODE_ENV !== 'production',
    logToConsole: process.env.NODE_ENV !== 'production',
    persistLogs: true,
    maxLogEntries: 100,
    includeTimestamps: true,
    logLevel: process.env.NODE_ENV === 'production' ? 'error' : 'debug',
  };

  private constructor() {
    // Initialize error handler
    this.setupGlobalErrorHandler();
    
    // Restore persisted logs if available
    this.restoreLogs();
    
    console.log('🛠️ Debug Manager initialized');
  }

  /**
   * Get the singleton instance
   */
  public static getInstance(): DebugManager {
    if (!DebugManager.instance) {
      DebugManager.instance = new DebugManager();
    }
    return DebugManager.instance;
  }

  /**
   * Configure debug options
   */
  public configure(options: Partial<DebugOptions>): void {
    this.options = { ...this.options, ...options };
    
    // Log configuration
    if (this.options.enabled) {
      console.log('Debug Manager configuration updated:', this.options);
    }
  }

  /**
   * Log a message
   */
  public log(level: LogLevel, component: string, message: string, data?: any): void {
    if (!this.options.enabled) return;
    
    // Check log level
    if (!this.shouldLog(level)) return;
    
    // Create log entry
    const entry = { 
      level, 
      component, 
      message, 
      data, 
      timestamp: new Date() 
    };
    
    // Store log
    this.logs.push(entry);
    
    // Trim logs if over max
    if (this.logs.length > this.options.maxLogEntries) {
      this.logs = this.logs.slice(-this.options.maxLogEntries);
    }
    
    // Log to console if enabled
    if (this.options.logToConsole) {
      const timestamp = this.options.includeTimestamps ? 
        `[${new Date().toISOString()}] ` : '';
      
      const componentStr = component ? `[${component}] ` : '';
      
      // Use appropriate console method
      switch (level) {
        case 'debug':
          console.debug(`${timestamp}${componentStr}${message}`, data || '');
          break;
        case 'info':
          console.info(`${timestamp}${componentStr}${message}`, data || '');
          break;
        case 'warn':
          console.warn(`${timestamp}${componentStr}${message}`, data || '');
          break;
        case 'error':
          console.error(`${timestamp}${componentStr}${message}`, data || '');
          break;
      }
    }
    
    // Persist logs if enabled
    if (this.options.persistLogs) {
      this.persistLogs();
    }
  }
  
  /**
   * Debug level log
   */
  public debug(component: string, message: string, data?: any): void {
    this.log('debug', component, message, data);
  }
  
  /**
   * Info level log
   */
  public info(component: string, message: string, data?: any): void {
    this.log('info', component, message, data);
  }
  
  /**
   * Warning level log
   */
  public warn(component: string, message: string, data?: any): void {
    this.log('warn', component, message, data);
  }
  
  /**
   * Error level log
   */
  public error(component: string, message: string, data?: any): void {
    this.log('error', component, message, data);
  }
  
  /**
   * Track an error
   */
  public trackError(error: Error, component: string): void {
    if (!this.options.enabled) return;
    
    // Create error entry
    const entry = { 
      error, 
      component, 
      timestamp: new Date() 
    };
    
    // Store error
    this.errors.push(entry);
    
    // Log the error
    this.log('error', component, error.message, { 
      stack: error.stack,
      name: error.name
    });
    
    // Try to send error to a monitoring service or store it
    this.persistErrors();
  }
  
  /**
   * Start performance measurement
   */
  public startPerformanceMark(name: string): void {
    if (!this.options.enabled) return;
    
    this.performanceMarks[name] = performance.now();
  }
  
  /**
   * End performance measurement
   */
  public endPerformanceMark(name: string, component: string): number {
    if (!this.options.enabled || !this.performanceMarks[name]) return 0;
    
    const startTime = this.performanceMarks[name];
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Log the performance
    this.log('debug', component, `Performance: ${name}`, { 
      duration: `${duration.toFixed(2)}ms`,
      startTime,
      endTime
    });
    
    // Cleanup
    delete this.performanceMarks[name];
    
    return duration;
  }
  
  /**
   * Get all logs
   */
  public getLogs(): Array<{level: LogLevel, component: string, message: string, data?: any, timestamp: Date}> {
    return [...this.logs];
  }
  
  /**
   * Get all errors
   */
  public getErrors(): Array<{error: Error, component: string, timestamp: Date}> {
    return [...this.errors];
  }
  
  /**
   * Clear logs
   */
  public clearLogs(): void {
    this.logs = [];
    this.persistLogs();
  }
  
  /**
   * Check if the given level should be logged
   */
  private shouldLog(level: LogLevel): boolean {
    const levels: LogLevel[] = ['debug', 'info', 'warn', 'error'];
    const configLevelIndex = levels.indexOf(this.options.logLevel);
    const logLevelIndex = levels.indexOf(level);
    
    return logLevelIndex >= configLevelIndex;
  }
  
  /**
   * Setup global error handler
   */
  private setupGlobalErrorHandler(): void {
    if (typeof window !== 'undefined') {
      const originalOnError = window.onerror;
      
      window.onerror = (message, source, lineno, colno, error) => {
        // Track error
        if (error) {
          this.trackError(error, 'GlobalErrorHandler');
        } else {
          this.error('GlobalErrorHandler', typeof message === 'string' ? message : 'Unknown error');
        }
        
        // Call original handler
        if (typeof originalOnError === 'function') {
          return originalOnError(message, source, lineno, colno, error);
        }
        
        return false;
      };
      
      // Unhandled promise rejection handler
      const originalOnUnhandledRejection = window.onunhandledrejection;
      
      window.onunhandledrejection = (event) => {
        // Track error
        if (event.reason instanceof Error) {
          this.trackError(event.reason, 'UnhandledPromiseRejection');
        } else {
          this.error(
            'UnhandledPromiseRejection', 
            typeof event.reason === 'string' ? event.reason : 'Unknown promise rejection'
          );
        }
        
        // Call original handler
        if (typeof originalOnUnhandledRejection === 'function') {
          originalOnUnhandledRejection.call(window, event);
        }
      };
    }
  }
  
  /**
   * Persist logs to localStorage
   */
  private persistLogs(): void {
    if (!this.options.persistLogs || typeof localStorage === 'undefined') return;
    
    try {
      // Only store the last 50 logs to avoid overflowing storage
      const logsToStore = this.logs.slice(-50);
      localStorage.setItem('debug_logs', JSON.stringify(logsToStore));
    } catch (err) {
      // Silent fail if storage is full
      console.warn('Failed to persist logs:', err);
    }
  }
  
  /**
   * Persist errors to localStorage
   */
  private persistErrors(): void {
    if (!this.options.persistLogs || typeof localStorage === 'undefined') return;
    
    try {
      // Only store the last 20 errors to avoid overflowing storage
      const errorsToStore = this.errors.slice(-20).map(entry => ({
        message: entry.error.message,
        name: entry.error.name,
        stack: entry.error.stack,
        component: entry.component,
        timestamp: entry.timestamp
      }));
      
      localStorage.setItem('debug_errors', JSON.stringify(errorsToStore));
    } catch (err) {
      // Silent fail if storage is full
      console.warn('Failed to persist errors:', err);
    }
  }
  
  /**
   * Restore logs from localStorage
   */
  private restoreLogs(): void {
    if (!this.options.persistLogs || typeof localStorage === 'undefined') return;
    
    try {
      const storedLogs = localStorage.getItem('debug_logs');
      if (storedLogs) {
        this.logs = JSON.parse(storedLogs);
        
        // Convert string timestamps back to Date objects
        this.logs = this.logs.map(log => ({
          ...log,
          timestamp: new Date(log.timestamp)
        }));
      }
    } catch (err) {
      console.warn('Failed to restore logs:', err);
    }
  }
}

// Export singleton instance
export const debugManager = DebugManager.getInstance();

// Export convenience functions
export const debug = (component: string, message: string, data?: any) => 
  debugManager.debug(component, message, data);

export const info = (component: string, message: string, data?: any) => 
  debugManager.info(component, message, data);

export const warn = (component: string, message: string, data?: any) => 
  debugManager.warn(component, message, data);

export const error = (component: string, message: string, data?: any) => 
  debugManager.error(component, message, data);

export const trackError = (error: Error, component: string) => 
  debugManager.trackError(error, component);

export const startPerformanceMark = (name: string) => 
  debugManager.startPerformanceMark(name);

export const endPerformanceMark = (name: string, component: string) => 
  debugManager.endPerformanceMark(name, component);
